// The sales board's charts, drawn in real pixels.
//
// They measure the box they sit in and draw to it, rather than scaling a fixed
// viewBox: a stretched viewBox stretches its text too, and a chart that fills a
// panel of any shape must keep its labels at the size of the rest of the page.

import { useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./SalesPage.module.css";
import type { Column, PartnerSlot } from "./salesBoard.js";
import { axisScale, ticks } from "../../design/chart/scale.js";

/** The box an element is drawn into, in CSS pixels; zero until measured. */
export function useBoxSize(): [
  React.RefObject<HTMLDivElement | null>,
  { width: number; height: number },
] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;
      const { width, height } = entry.contentRect;
      setSize((was) =>
        Math.round(was.width) === Math.round(width) && Math.round(was.height) === Math.round(height)
          ? was
          : { width, height },
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

const MARGIN = { top: 22, right: 4, bottom: 26, left: 52 };
const MAX_BAR = 46;
const GAP = 2;
const RADIUS = 3;

/**
 * Months as columns, each stacked by partner, the biggest partner at the base.
 * Picking a partner (`focus`) greys the rest and puts its own figure on top of
 * each column; hovering a column lists every partner in it.
 */
export function PartnerColumns({
  columns,
  focus,
  money,
  label,
}: {
  columns: readonly Column[];
  focus: string | null;
  money: (minor: number) => string;
  label: string;
}): ReactNode {
  const [box, size] = useBoxSize();
  const [hover, setHover] = useState<number | null>(null);
  const plotWidth = Math.max(0, size.width - MARGIN.left - MARGIN.right);
  const plotHeight = Math.max(0, size.height - MARGIN.top - MARGIN.bottom);
  const shown = (column: Column) =>
    focus === null ? column.total : (column.segments.find((s) => s.slot.id === focus)?.value ?? 0);
  const { top } = axisScale(Math.max(1, ...columns.map((column) => column.total)));
  const y = (value: number) => MARGIN.top + plotHeight - (value / top) * plotHeight;
  const slot = columns.length === 0 ? 0 : plotWidth / columns.length;
  const barWidth = Math.min(MAX_BAR, slot * 0.58);
  const hovered = hover === null ? undefined : columns[hover];

  return (
    <div className={styles.plot} ref={box} onMouseLeave={() => setHover(null)}>
      {size.width > 0 && size.height > 0 && (
        <svg width={size.width} height={size.height} role="img" aria-label={label}>
          {ticks(top).map((tick) => (
            <g key={tick}>
              <line
                className={tick === 0 ? styles.baseline : styles.grid}
                x1={MARGIN.left}
                x2={size.width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
              />
              <text className={styles.axis} x={MARGIN.left - 8} y={y(tick) + 4} textAnchor="end">
                {money(tick)}
              </text>
            </g>
          ))}
          {columns.map((column, index) => {
            const cx = MARGIN.left + slot * index + slot / 2;
            const x = cx - barWidth / 2;
            let base = 0;
            // A picked partner is drawn at the base, so its months read against
            // one baseline like a chart of its own.
            const drawn = column.segments
              .filter((segment) => segment.value > 0)
              .sort((a, b) => (a.slot.id === focus ? -1 : b.slot.id === focus ? 1 : 0));
            const focusTop =
              focus === null
                ? column.total
                : drawn[0]?.slot.id === focus
                  ? (drawn[0]?.value ?? 0)
                  : 0;
            return (
              <g key={column.key}>
                {hover === index && (
                  <rect
                    className={styles.hoverBand}
                    x={MARGIN.left + slot * index}
                    y={MARGIN.top - 18}
                    width={slot}
                    height={plotHeight + 18}
                  />
                )}
                {drawn.map((segment, i) => {
                  const y0 = y(base);
                  base += segment.value;
                  const y1 = y(base);
                  const isTop = i === drawn.length - 1;
                  // The surface gap sits on top of each segment, so the stack keeps
                  // its true height at the base and partners never touch.
                  const height = Math.max(0, y0 - y1 - (isTop ? 0 : GAP));
                  const fill =
                    focus === null || focus === segment.slot.id
                      ? segment.slot.colour
                      : "var(--series-muted)";
                  return (
                    <path
                      key={segment.slot.id}
                      d={
                        isTop
                          ? roundedTop(x, y1, barWidth, height)
                          : rect(x, y0 - height, barWidth, height)
                      }
                      fill={fill}
                    />
                  );
                })}
                {shown(column) > 0 && (
                  <text
                    className={styles.columnTotal}
                    x={cx}
                    y={y(focusTop) - 6}
                    textAnchor="middle"
                  >
                    {money(shown(column))}
                  </text>
                )}
                <text className={styles.axis} x={cx} y={size.height - 8} textAnchor="middle">
                  {column.tick}
                </text>
                <rect
                  x={MARGIN.left + slot * index}
                  y={0}
                  width={slot}
                  height={size.height}
                  fill="transparent"
                  onMouseEnter={() => setHover(index)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {hovered !== undefined && hover !== null && (
        <ColumnTip
          column={hovered}
          money={money}
          focus={focus}
          left={MARGIN.left + slot * hover + slot / 2}
          width={size.width}
        />
      )}
      <div className="srOnly">
        <table>
          <caption>{label}</caption>
          <tbody>
            {columns.map((column) => (
              <tr key={column.key}>
                <th scope="row">{column.label}</th>
                <td>{money(column.total)}</td>
                {column.segments.map((segment) => (
                  <td key={segment.slot.id}>{`${segment.slot.name} ${money(segment.value)}`}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function rect(x: number, y: number, w: number, h: number): string {
  return `M${x},${y}h${w}v${h}h${-w}Z`;
}

/** A bar end rounded at the top and square at the base. */
function roundedTop(x: number, y: number, w: number, h: number): string {
  const r = Math.min(RADIUS, h, w / 2);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

const TIP_WIDTH = 220;

function ColumnTip({
  column,
  money,
  focus,
  left,
  width,
}: {
  column: Column;
  money: (minor: number) => string;
  focus: string | null;
  left: number;
  width: number;
}): ReactNode {
  // Beside the column, on whichever side has room.
  const x = left + 32 + TIP_WIDTH > width ? left - 32 - TIP_WIDTH : left + 32;
  const rows =
    column.detail?.rows.map((row) => ({
      key: row.id,
      name: row.name,
      colour: row.colour,
      value: row.value,
      id: row.id,
    })) ??
    [...column.segments]
      .filter((segment) => segment.value > 0)
      .reverse()
      .map((segment) => ({
        key: segment.slot.id,
        name: segment.slot.name,
        colour: segment.slot.colour,
        value: segment.value,
        id: segment.slot.id,
      }));
  const dimmed = (id: string) => focus !== null && focus !== id && column.detail === undefined;
  return (
    <div className={styles.tip} style={{ left: x, width: TIP_WIDTH }} role="tooltip">
      <div className={styles.tipHead}>
        <span>{column.label}</span>
        <span className={styles.tipTotal}>{money(column.total)}</span>
      </div>
      {rows.length === 0 && <div className={styles.tipRow}>Nothing</div>}
      {rows.map((row) => (
        <div key={row.key} className={`${styles.tipRow} ${dimmed(row.id) ? styles.tipDim : ""}`}>
          <span className={styles.swatch} style={{ background: row.colour }} />
          <span className={styles.tipName}>{row.name}</span>
          <span className={styles.tipValue}>{money(row.value)}</span>
        </div>
      ))}
      {column.detail !== undefined && column.detail.moreCount > 0 && (
        <div className={`${styles.tipRow} ${styles.tipMore}`}>
          <span className={styles.tipName}>{`${column.detail.moreCount} more`}</span>
          <span className={styles.tipValue}>{money(column.detail.moreValue)}</span>
        </div>
      )}
    </div>
  );
}

/** Partners as a row of keys; one clicked is picked out in the chart, clicked again let go. */
export function PartnerLegend({
  slots,
  focus,
  onFocus,
}: {
  slots: readonly PartnerSlot[];
  focus: string | null;
  onFocus: (id: string | null) => void;
}): ReactNode {
  return (
    <div className={styles.legend}>
      {slots.map((slot) => (
        <button
          key={slot.id}
          type="button"
          className={`${styles.legendItem} ${focus !== null && focus !== slot.id ? styles.legendDim : ""}`}
          aria-pressed={focus === slot.id}
          onClick={() => onFocus(focus === slot.id ? null : slot.id)}
        >
          <span className={styles.swatch} style={{ background: slot.colour }} aria-hidden="true" />
          {slot.name}
        </button>
      ))}
    </div>
  );
}

/** A figure's last twelve months as a line, the latest month marked. */
export function Sparkline({ values }: { values: readonly number[] }): ReactNode {
  const width = 96;
  const height = 28;
  if (values.length < 2)
    return <svg className={styles.spark} width={width} height={height} aria-hidden="true" />;
  const min = Math.min(0, ...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i: number) => 3 + (i / (values.length - 1)) * (width - 6);
  const y = (v: number) => height - 3 - ((v - min) / span) * (height - 6);
  const line = values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`).join("");
  const lastIndex = values.length - 1;
  return (
    <svg className={styles.spark} width={width} height={height} aria-hidden="true">
      <path
        d={`${line}L${x(lastIndex)},${height - 3}L${x(0)},${height - 3}Z`}
        className={styles.sparkArea}
      />
      <path d={line} className={styles.sparkLine} />
      <circle cx={x(lastIndex)} cy={y(values[lastIndex] ?? 0)} r={3} className={styles.sparkDot} />
    </svg>
  );
}
